import { useState, useEffect, useCallback } from "react";
import { devicesService } from "@/services";
import type { UserDevice, DeviceApprovalStatus } from "@/services/types";
import { PageHeader } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { Modal } from "@/components/base/Modal";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { Avatar } from "@/components/base/Avatar";
import { SettingsNav } from "../components/SettingsNav";
import { useAuth } from "@/context/AuthContext";
import { canManageDevices, roleLabel } from "@/config/roles";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";
import { formatDateTime } from "@/utils/format";

type FilterStatus = "ALL" | DeviceApprovalStatus;

export default function DevicesSettingsPage() {
  const { role } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const canManage = canManageDevices(role);

  const [devices, setDevices] = useState<UserDevice[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<FilterStatus>("ALL");
  const [search, setSearch] = useState("");

  // Action modals
  const [approveTarget, setApproveTarget] = useState<UserDevice | null>(null);
  const [approveLabel, setApproveLabel] = useState("");
  const [approving, setApproving] = useState(false);

  const [rejectTarget, setRejectTarget] = useState<UserDevice | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const [editTarget, setEditTarget] = useState<UserDevice | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [savingLabel, setSavingLabel] = useState(false);

  const [actingId, setActingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await devicesService.listDeviceRequests({
        status: statusFilter === "ALL" ? undefined : statusFilter,
        search: search.trim() || undefined,
        limit: 50,
      });
      setDevices(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load IP requests.");
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openApproveModal = (device: UserDevice) => {
    setApproveTarget(device);
    setApproveLabel(device.deviceLabel || `${device.browser || "System"} on ${device.os || "OS"}`);
  };

  const handleApprove = async () => {
    if (!approveTarget) return;
    setApproving(true);
    try {
      const res = await devicesService.approveDevice(approveTarget.id, approveLabel);
      if (res.autoRevokedDevice) {
        toast.warning(
          "IP Approved (1-IP Limit)",
          `Approved ${approveTarget.userName}'s IP. Their previous IP (${res.autoRevokedDevice.label || "Previous IP"}) was automatically revoked.`
        );
      } else {
        toast.success("IP Approved", `${approveTarget.userName}'s IP (${approveTarget.ipAddress || "system"}) is now authorized.`);
      }
      setApproveTarget(null);
      await load();
    } catch (err) {
      toast.error("Could not approve IP", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setApproving(false);
    }
  };

  const openRejectModal = (device: UserDevice) => {
    setRejectTarget(device);
    setRejectReason("");
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await devicesService.rejectDevice(rejectTarget.id, rejectReason.trim() || undefined);
      toast.info("IP Request Rejected", `Rejected IP request for ${rejectTarget.userName}.`);
      setRejectTarget(null);
      await load();
    } catch (err) {
      toast.error("Could not reject IP", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setRejecting(false);
    }
  };

  const handleRevoke = async (device: UserDevice) => {
    const ok = await confirm({
      title: "Revoke IP authorization?",
      message: `Revoking this IP will immediately log out ${device.userName} and block future logins from this IP address until re-approved.`,
      confirmLabel: "Revoke IP",
      destructive: true,
    });
    if (!ok) return;

    setActingId(device.id);
    try {
      await devicesService.revokeDevice(device.id, "Revoked by Administrator");
      toast.success("IP Revoked", `Revoked access for ${device.userName}'s IP (${device.ipAddress}).`);
      await load();
    } catch (err) {
      toast.error("Could not revoke IP", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setActingId(null);
    }
  };

  const handleDelete = async (device: UserDevice) => {
    const ok = await confirm({
      title: "Delete IP Record?",
      message: `Permanently delete IP record ${device.ipAddress} for ${device.userName}? If deleted, any active session from this IP will be ended, and the user will need new approval to access again.`,
      confirmLabel: "Delete IP",
      destructive: true,
    });
    if (!ok) return;

    setActingId(device.id);
    try {
      await devicesService.deleteDevice(device.id);
      toast.success("IP Deleted", `Deleted IP record for ${device.userName} (${device.ipAddress}).`);
      await load();
    } catch (err) {
      toast.error("Could not delete IP", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setActingId(null);
    }
  };

  const openEditLabel = (device: UserDevice) => {
    setEditTarget(device);
    setEditLabel(device.deviceLabel || "");
  };

  const handleSaveLabel = async () => {
    if (!editTarget) return;
    setSavingLabel(true);
    try {
      await devicesService.updateDeviceLabel(editTarget.id, editLabel);
      toast.success("Label Updated", "The system nickname has been updated.");
      setEditTarget(null);
      await load();
    } catch (err) {
      toast.error("Could not update label", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSavingLabel(false);
    }
  };

  const pendingCount = (devices ?? []).filter((d) => d.status === "PENDING").length;
  const approvedCount = (devices ?? []).filter((d) => d.status === "APPROVED").length;
  const rejectedCount = (devices ?? []).filter((d) => d.status === "REJECTED" || d.status === "REVOKED").length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="IP Approvals"
        description="Review and authorize staff IP address and system access. Maximum 1 active IP permitted per user."
      />

      <SettingsNav />

      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card className="p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-500">Pending Approvals</span>
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-warning-100 text-warning-700">
              <i className="ri-time-line text-base" />
            </span>
          </div>
          <div className="mt-2 text-xl font-bold text-foreground-900">{pendingCount}</div>
          <p className="mt-0.5 text-[11px] text-foreground-500">Awaiting administrator verification</p>
        </Card>

        <Card className="p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-500">Approved IPs</span>
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-success-100 text-success-700">
              <i className="ri-shield-check-line text-base" />
            </span>
          </div>
          <div className="mt-2 text-xl font-bold text-foreground-900">{approvedCount}</div>
          <p className="mt-0.5 text-[11px] text-foreground-500">Active authorized staff IP addresses</p>
        </Card>

        <Card className="p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-500">Rejected / Revoked</span>
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-background-200 text-foreground-600">
              <i className="ri-shield-cross-line text-base" />
            </span>
          </div>
          <div className="mt-2 text-xl font-bold text-foreground-900">{rejectedCount}</div>
          <p className="mt-0.5 text-[11px] text-foreground-500">Restricted IP address access</p>
        </Card>
      </div>

      {/* Filter and Search Bar */}
      <Card className="p-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-1.5">
            {(["ALL", "PENDING", "APPROVED", "REJECTED", "REVOKED"] as FilterStatus[]).map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setStatusFilter(st)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  statusFilter === st
                    ? "bg-primary-600 text-white shadow-sm"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
                }`}
              >
                {st === "ALL" ? "All Requests" : st.charAt(0) + st.slice(1).toLowerCase()}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-64">
            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-foreground-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search user, IP, or browser..."
              className="h-8 w-full rounded-md border border-background-300 bg-background-50 pl-7 pr-3 text-xs text-foreground-800 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>
      </Card>

      {/* Devices/IP List Table */}
      <Card className="overflow-hidden">
        {loading && !devices ? (
          <div className="flex h-48 items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
          </div>
        ) : error ? (
          <div className="p-6">
            <ErrorState title="Failed to load IP requests" description={error} onRetry={load} />
          </div>
        ) : !devices || devices.length === 0 ? (
          <div className="p-8">
            <EmptyState
              title="No IP requests found"
              description={
                search || statusFilter !== "ALL"
                  ? "No requests match the selected filters."
                  : "When staff members sign in from a new IP address, their authorization requests will appear here."
              }
              icon="ri-router-line"
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-background-200 bg-background-100/50 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                <tr>
                  <th className="px-4 py-3">Staff Member</th>
                  <th className="px-4 py-3">IP Address</th>
                  <th className="px-4 py-3">System / Browser</th>
                  <th className="px-4 py-3">Requested / Last Active</th>
                  <th className="px-4 py-3">Status</th>
                  {canManage ? <th className="px-4 py-3 text-right">Actions</th> : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {devices.map((device) => {
                  const isPending = device.status === "PENDING";
                  const isApproved = device.status === "APPROVED";
                  const isRejected = device.status === "REJECTED";

                  return (
                    <tr key={device.id} className="hover:bg-background-50/70 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={device.userName || "User"} size="sm" />
                          <div>
                            <div className="font-medium text-foreground-900">{device.userName || "Staff User"}</div>
                            <div className="text-[11px] text-foreground-500">{device.userEmail}</div>
                            {device.userRole ? (
                              <span className="mt-0.5 inline-block text-[10px] text-foreground-400">
                                {roleLabel(device.userRole)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 font-mono text-xs font-semibold text-foreground-900">
                          <i className="ri-router-line text-sm text-primary-500" />
                          <span>{device.ipAddress || "—"}</span>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5 font-medium text-foreground-800">
                            <span>{device.deviceLabel || `${device.browser || "Browser"} on ${device.os || "Device"}`}</span>
                            {canManage && isApproved ? (
                              <button
                                type="button"
                                onClick={() => openEditLabel(device)}
                                title="Edit nickname"
                                className="text-foreground-400 hover:text-foreground-600"
                              >
                                <i className="ri-pencil-line text-xs" />
                              </button>
                            ) : null}
                          </div>
                          <div className="text-[11px] text-foreground-500">
                            {device.browser} • {device.os}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          <div className="text-foreground-700">
                            {formatDateTime(device.requestedAt)}
                          </div>
                          {device.lastUsedAt ? (
                            <div className="text-[10px] text-foreground-400">
                              Active: {formatDateTime(device.lastUsedAt)}
                            </div>
                          ) : null}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        {isPending ? (
                          <Badge variant="warning">
                            <span className="mr-1 h-1.5 w-1.5 animate-pulse rounded-full bg-warning-500 inline-block" />
                            Pending
                          </Badge>
                        ) : isApproved ? (
                          <Badge variant="success">Approved</Badge>
                        ) : isRejected ? (
                          <Badge variant="danger">Rejected</Badge>
                        ) : (
                          <Badge variant="neutral">Revoked</Badge>
                        )}
                        {device.rejectReason ? (
                          <p className="mt-1 text-[10px] text-danger-600 truncate max-w-[150px]" title={device.rejectReason}>
                            {device.rejectReason}
                          </p>
                        ) : null}
                      </td>

                      {canManage ? (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {isPending ? (
                              <>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => openApproveModal(device)}
                                >
                                  Approve
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => openRejectModal(device)}
                                >
                                  Reject
                                </Button>
                              </>
                            ) : isApproved ? (
                              <Button
                                variant="secondary"
                                size="sm"
                                loading={actingId === device.id}
                                onClick={() => handleRevoke(device)}
                              >
                                Revoke
                              </Button>
                            ) : (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => openApproveModal(device)}
                              >
                                Re-approve
                              </Button>
                            )}

                            <Button
                              variant="outline"
                              size="sm"
                              iconOnly
                              loading={actingId === device.id}
                              onClick={() => handleDelete(device)}
                              title="Delete IP Record"
                              className="border-danger-200 text-danger-600 hover:bg-danger-50 hover:border-danger-300"
                              icon={<i className="ri-delete-bin-line text-sm" />}
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Approve Modal */}
      {approveTarget ? (
        <Modal
          open={Boolean(approveTarget)}
          onClose={() => setApproveTarget(null)}
          title="Authorize IP Address"
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setApproveTarget(null)} disabled={approving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleApprove} loading={approving}>
                Approve IP
              </Button>
            </div>
          }
        >
          <div className="space-y-4 text-xs">
            <p className="text-foreground-600">
              Grant authorization for <strong className="text-foreground-900">{approveTarget.userName}</strong> to access M.K. FINANCIAL SERVICES from this IP address.
            </p>

            <div className="rounded-md border border-background-200 bg-background-100 p-3 space-y-1.5">
              <div className="flex justify-between">
                <span className="text-foreground-500">IP Address:</span>
                <span className="font-mono font-bold text-primary-700">{approveTarget.ipAddress || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-500">Browser & OS:</span>
                <span className="font-medium text-foreground-800">
                  {approveTarget.browser} on {approveTarget.os}
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="approve-label" className="block font-medium text-foreground-700">
                Location / Nickname (Optional)
              </label>
              <input
                id="approve-label"
                type="text"
                value={approveLabel}
                onChange={(e) => setApproveLabel(e.target.value)}
                placeholder="e.g. Head Office Network, Main Branch"
                className="h-8 w-full rounded-md border border-background-300 bg-background-50 px-3 text-xs text-foreground-800 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <p className="text-[11px] text-foreground-400">
                A recognizable label to identify this authorization.
              </p>
            </div>

            <div className="rounded-md bg-info-50 p-2.5 text-info-800 text-[11px] flex items-start gap-2">
              <i className="ri-information-line text-sm mt-0.5 text-info-600 flex-none" />
              <span>
                Users can have up to 1 approved IP address. Approving this IP will automatically revoke their previously approved IP address.
              </span>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* Reject Modal */}
      {rejectTarget ? (
        <Modal
          open={Boolean(rejectTarget)}
          onClose={() => setRejectTarget(null)}
          title="Reject IP Request"
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setRejectTarget(null)} disabled={rejecting}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleReject} loading={rejecting}>
                Reject Request
              </Button>
            </div>
          }
        >
          <div className="space-y-4 text-xs">
            <p className="text-foreground-600">
              Reject the login request for <strong className="text-foreground-900">{rejectTarget.userName}</strong> from IP <strong className="font-mono text-foreground-800">{rejectTarget.ipAddress}</strong> ({rejectTarget.browser} on {rejectTarget.os}).
            </p>

            <div className="space-y-1.5">
              <label htmlFor="reject-reason" className="block font-medium text-foreground-700">
                Rejection Reason (Optional)
              </label>
              <textarea
                id="reject-reason"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Unrecognized network location, employee not scheduled"
                className="w-full rounded-md border border-background-300 bg-background-50 p-2.5 text-xs text-foreground-800 placeholder:text-foreground-400 focus:border-danger-500 focus:outline-none focus:ring-1 focus:ring-danger-500"
              />
              <p className="text-[11px] text-foreground-400">
                This note will be shown to the staff member when they attempt to sign in.
              </p>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* Edit Label Modal */}
      {editTarget ? (
        <Modal
          open={Boolean(editTarget)}
          onClose={() => setEditTarget(null)}
          title="Edit System Nickname"
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditTarget(null)} disabled={savingLabel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveLabel} loading={savingLabel}>
                Save Label
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-xs">
            <div className="space-y-1.5">
              <label htmlFor="edit-label" className="block font-medium text-foreground-700">
                System Nickname
              </label>
              <input
                id="edit-label"
                type="text"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                placeholder="e.g. Front Office Network"
                className="h-8 w-full rounded-md border border-background-300 bg-background-50 px-3 text-xs text-foreground-800 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
